// batch-66 contract-mijlpalen (FO-496) — PURE planner en regels van de
// mijlpaal-workflow rond het arbeidscontract.
//
// Geen DB, geen IO: de motor (`apps/api/src/services/contract-mijlpalen.ts`)
// levert de contractgegevens en het sjabloon aan en voert uit wat hier wordt
// uitgerekend. Alle datums zijn NL-kalenderdagen ('yyyy-mm-dd'); de enige plek
// waar een UTC-instant binnenkomt is het moment van ondertekening, dat hier naar
// de Europe/Amsterdam-dag wordt omgezet. Werkdagen-logica (weekend, feestdagen)
// zit uitsluitend in dit bestand, niet in de motor.
//
// Conventie voor contractdatums (zie ketenregeling.ts): `eindDatum` is de laatste
// dag van het contract, inclusief. Het einde van de proeftijd volgt dezelfde
// conventie: het is de LAATSTE dag van de proeftijd. Een proeftijd van een maand
// die op 2 november ingaat loopt tot en met 1 december; een beeindiging op
// 2 december is te laat. (Spec 196 T1 noemt "02-12": dat is "start plus een
// maand", de eerste dag NA de proeftijd.)

import {
  CONTRACT_GESPREK_TYPE_KEYS,
  CONTRACT_MIJLPAAL_FASE_PER_CODE,
  CONTRACT_MIJLPAAL_INHAAL_DAGEN,
  CONTRACT_MIJLPAAL_FORMULIER_PER_CODE,
  PROEFTIJD_GESPREK_WERKDAGEN_VOORAF,
  PROEFTIJD_REGEL,
  type ContractMijlpaalActie,
  type ContractMijlpaalAnker,
  type ContractMijlpaalBlokkade,
  type ContractMijlpaalCode,
  type ContractMijlpaalOffsetEenheid,
  type ContractMijlpaalOverslaanReden,
  type ContractMijlpaalSjabloonRegelBasis,
  type ContractMijlpaalStatus,
  type ContractMijlpaalVariant,
  type ContractMijlpaalWerkdagCorrectie,
  type ContractProeftijdUitkomst,
  type ProeftijdRegel,
} from '@streep/types'
import { amsterdamWallclockToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'
import { formatDagDatum, parseDagDatum } from './dagtaken-datum'
import { isWerkdag } from './uren-calculations'

// ─── Datumhulpen (NL-kalenderdagen) ───────────────────────────────────────────

/** `dag` plus `n` kalenderdagen (n mag negatief zijn). */
export function contractDagPlusDagen(dag: string, n: number): string {
  const d = parseDagDatum(dag)
  d.setUTCDate(d.getUTCDate() + n)
  return formatDagDatum(d)
}

/**
 * `dag` plus `n` kalendermaanden (n mag negatief zijn). Klemt op de laatste dag
 * van de doelmaand, zodat 31 januari plus een maand 28 of 29 februari wordt en
 * niet doorschuift naar maart.
 */
export function contractDagPlusMaanden(dag: string, n: number): string {
  const d = parseDagDatum(dag)
  const jaar = d.getUTCFullYear()
  const maand = d.getUTCMonth() + n
  const laatsteDag = new Date(Date.UTC(jaar, maand + 1, 0)).getUTCDate()
  return formatDagDatum(new Date(Date.UTC(jaar, maand, Math.min(d.getUTCDate(), laatsteDag))))
}

function feestdagenAlsDatums(feestdagen: readonly string[] | undefined): Date[] {
  const uit: Date[] = []
  for (const f of feestdagen ?? []) {
    try {
      uit.push(parseDagDatum(f))
    } catch {
      // Een ongeldige feestdag in de invoer mag de planning niet breken.
    }
  }
  return uit
}

function isContractWerkdag(dag: string, feestdagen: Date[]): boolean {
  return isWerkdag(parseDagDatum(dag), feestdagen)
}

/** De werkdag op of voor `dag` (ma-vr, geen feestdag). */
function werkdagOpOfVoor(dag: string, feestdagen: Date[]): string {
  let huidig = dag
  for (let i = 0; i < 31 && !isContractWerkdag(huidig, feestdagen); i++) {
    huidig = contractDagPlusDagen(huidig, -1)
  }
  return huidig
}

/** De werkdag op of na `dag` (ma-vr, geen feestdag). */
function werkdagOpOfNa(dag: string, feestdagen: Date[]): string {
  let huidig = dag
  for (let i = 0; i < 31 && !isContractWerkdag(huidig, feestdagen); i++) {
    huidig = contractDagPlusDagen(huidig, 1)
  }
  return huidig
}

function pasWerkdagCorrectieToe(
  dag: string,
  correctie: ContractMijlpaalWerkdagCorrectie,
  feestdagen: Date[],
): string {
  if (correctie === 'VORIGE_WERKDAG') return werkdagOpOfVoor(dag, feestdagen)
  if (correctie === 'VOLGENDE_WERKDAG') return werkdagOpOfNa(dag, feestdagen)
  return dag
}

/** De dag `werkdagen` werkdagen voor `dag` (0 = de dag zelf). */
export function contractWerkdagenTerug(
  dag: string,
  werkdagen: number,
  feestdagen: readonly string[] = [],
): string {
  const vrij = feestdagenAlsDatums(feestdagen)
  let huidig = dag
  let geteld = 0
  const doel = Math.max(0, Math.floor(werkdagen))
  for (let i = 0; i < 400 && geteld < doel; i++) {
    huidig = contractDagPlusDagen(huidig, -1)
    if (isContractWerkdag(huidig, vrij)) geteld += 1
  }
  return huidig
}

function pasOffsetToe(dag: string, aantal: number, eenheid: ContractMijlpaalOffsetEenheid): string {
  if (aantal === 0) return dag
  if (eenheid === 'MAANDEN') return contractDagPlusMaanden(dag, aantal)
  return contractDagPlusDagen(dag, eenheid === 'WEKEN' ? aantal * 7 : aantal)
}

/** De vrijdag van de eerste werkweek; een start in het weekend telt voor de week erna. */
export function contractEindeWeek1(startDatum: string): string {
  const weekdag = parseDagDatum(startDatum).getUTCDay() // 0 = zondag ... 6 = zaterdag
  return contractDagPlusDagen(startDatum, (5 - weekdag + 7) % 7)
}

/** Einde van de eerste maand: startdatum plus een kalendermaand. */
export function contractEindeMaand1(startDatum: string): string {
  return contractDagPlusMaanden(startDatum, 1)
}

/**
 * De laatste dag van een periode van `maanden` kalendermaanden die op
 * `startDatum` ingaat. De periode eindigt op de dag voor dezelfde dagnummer in de
 * doelmaand (2 november plus een maand: tot en met 1 december). Bestaat die
 * dagnummer daar niet (31 januari plus een maand), dan eindigt de periode op de
 * laatste dag van de doelmaand (28 of 29 februari).
 */
export function contractPeriodeLaatsteDag(startDatum: string, maanden: number): string {
  const doel = contractDagPlusMaanden(startDatum, maanden)
  const geklemd = parseDagDatum(doel).getUTCDate() !== parseDagDatum(startDatum).getUTCDate()
  return geklemd ? doel : contractDagPlusDagen(doel, -1)
}

/** NL-kalenderdag van een UTC-instant (Date of ISO-string). */
function nlDag(instant: Date | string): string {
  return utcToAmsterdamWallclock(typeof instant === 'string' ? new Date(instant) : instant).ymd
}

// ─── Proeftijd (spec §2.A.1) ──────────────────────────────────────────────────

export type ProeftijdGrond =
  | 'TOT_ZES_MAANDEN'
  | 'TOT_TWEE_JAAR'
  | 'LANGER_DAN_TWEE_JAAR'
  | 'ONBEPAALDE_TIJD'
  | 'VERLENGING'
  | 'CONTRACTDOCUMENT'

export interface ProeftijdAfleiding {
  /** Laatste dag van de proeftijd ('yyyy-mm-dd'), of null als er geen proeftijd is. */
  eindDatum: string | null
  maanden: number
  grond: ProeftijdGrond
}

export const PROEFTIJD_GROND_LABELS: Record<ProeftijdGrond, string> = {
  TOT_ZES_MAANDEN:      'Contract tot en met 6 maanden: geen proeftijd',
  TOT_TWEE_JAAR:        'Bepaalde tijd tot en met 2 jaar: 1 maand',
  LANGER_DAN_TWEE_JAAR: 'Bepaalde tijd langer dan 2 jaar: 2 maanden',
  ONBEPAALDE_TIJD:      'Onbepaalde tijd: 2 maanden',
  VERLENGING:           'Verlengingscontract: geen nieuwe proeftijd',
  CONTRACTDOCUMENT:     'Volgens het contractdocument',
}

/**
 * Leid het einde van de proeftijd af uit de contractduur. De uitkomst is de
 * laatste dag van de proeftijd (zie `contractPeriodeLaatsteDag`).
 *
 * De contractduur wordt inclusief de einddatum gemeten (de einddatum is de
 * laatste contractdag): 1 januari tot en met 30 juni is precies zes maanden en
 * kent dus geen proeftijd, 1 januari tot en met 1 juli wel.
 *
 * "Tot en met N maanden" toetsen we met dezelfde conventie als de proeftijd
 * zelf (`contractPeriodeLaatsteDag`): het contract duurt hoogstens N maanden als
 * de einddatum op of voor de laatste dag van die periode ligt. Dat klopt ook aan
 * het maandeinde: 31 augustus tot en met 28 februari is precies zes maanden (31
 * februari bestaat niet) en kent dus geen proeftijd. Een toets op "de dag na het
 * einde" tegen een geklemde datum zat daar een dag naast, en gaf zo'n contract
 * ten onrechte een maand proeftijd.
 *
 * `overeengekomenMaanden` is de proeftijd zoals die in het contractdocument
 * staat (de samenvoegwaarde "Proeftijd (maanden)"). Die kan de afgeleide
 * proeftijd alleen verkorten, nooit verlengen: een langere proeftijd dan de regel
 * toestaat nemen we niet over.
 */
export function leidProeftijdAf(
  invoer: {
    startDatum: string
    eindDatum: string | null
    isVerlenging?: boolean
    overeengekomenMaanden?: number | null
  },
  regel: ProeftijdRegel = PROEFTIJD_REGEL,
): ProeftijdAfleiding {
  if (invoer.isVerlenging) return { eindDatum: null, maanden: 0, grond: 'VERLENGING' }

  let maanden: number
  let grond: ProeftijdGrond
  if (invoer.eindDatum === null) {
    maanden = regel.langeProeftijdMaanden
    grond = 'ONBEPAALDE_TIJD'
  } else {
    if (invoer.eindDatum <= contractPeriodeLaatsteDag(invoer.startDatum, regel.geenProeftijdTotMaanden)) {
      maanden = 0
      grond = 'TOT_ZES_MAANDEN'
    } else if (
      invoer.eindDatum <= contractPeriodeLaatsteDag(invoer.startDatum, regel.korteProeftijdTotMaanden)
    ) {
      maanden = regel.korteProeftijdMaanden
      grond = 'TOT_TWEE_JAAR'
    } else {
      maanden = regel.langeProeftijdMaanden
      grond = 'LANGER_DAN_TWEE_JAAR'
    }
  }

  const overeengekomen = invoer.overeengekomenMaanden
  if (
    overeengekomen !== null &&
    overeengekomen !== undefined &&
    Number.isInteger(overeengekomen) &&
    overeengekomen >= 0 &&
    overeengekomen < maanden
  ) {
    maanden = overeengekomen
    grond = 'CONTRACTDOCUMENT'
  }

  return {
    eindDatum: maanden > 0 ? contractPeriodeLaatsteDag(invoer.startDatum, maanden) : null,
    maanden,
    grond,
  }
}

/** Uiterste dag voor het einde-proeftijdgesprek: 3 werkdagen voor de laatste dag van de proeftijd. */
export function uitersteProeftijdGesprekDag(
  proeftijdEindDatum: string,
  feestdagen: readonly string[] = [],
): string {
  return contractWerkdagenTerug(proeftijdEindDatum, PROEFTIJD_GESPREK_WERKDAGEN_VOORAF, feestdagen)
}

// ─── Planner ──────────────────────────────────────────────────────────────────

export interface MijlpaalContractInvoer {
  /**
   * Het moment waarop de reeks start: de ondertekening (UTC-instant), of bij een
   * contract zonder handtekening het moment van activeren. Null = nog niet.
   */
  ondertekendOp: Date | string | null
  /** Startdatum van het contract (NL-kalenderdag). */
  startDatum: string
  /** Laatste contractdag; null bij onbepaalde tijd. */
  eindDatum: string | null
  /** Laatste dag van de proeftijd; null als er geen proeftijd is. */
  proeftijdEindDatum: string | null
  /** Het contract vervangt een eerder contract (verlenging). */
  isVerlenging: boolean
}

export interface MijlpaalSjabloonInvoer {
  /** Een verlengingscontract doorloopt alleen de verlengingsreeks (M9). */
  verlengingAlleenEindreeks: boolean
  regels: readonly ContractMijlpaalSjabloonRegelBasis[]
}

export interface MijlpaalPlanOpties {
  /** Feestdagen als 'yyyy-mm-dd'; tellen niet als werkdag. */
  feestdagen?: readonly string[]
}

export interface GeplandeMijlpaal {
  code: ContractMijlpaalCode
  anker: ContractMijlpaalAnker
  actie: ContractMijlpaalActie
  /** Het moment van de mijlpaal; null wanneer hij is overgeslagen. */
  momentOp: string | null
  tijdstip: string | null
  /** Het moment als UTC-instant (dag plus tijdstip in Europe/Amsterdam). */
  momentInstant: Date | null
  /** Vanaf deze dag staat de mijlpaal open (moment minus de voorlooptijd). */
  opentOp: string | null
  /** Laatste dag waarop afronden nog op tijd is; null = geen termijn. */
  deadlineOp: string | null
  variant: ContractMijlpaalVariant
  /** Door de planner afgeleide reden om over te slaan; null = van toepassing. */
  overslaanReden: ContractMijlpaalOverslaanReden | null
  /** Gesprekstype dat bij deze mijlpaal gepland moet worden (variant-bewust). */
  gesprekTypeKey: string | null
  /** Harde uiterste dag voor dat gesprek (alleen de week-1-check met proeftijd). */
  gesprekUiterlijkOp: string | null
}

function ankerDag(
  anker: ContractMijlpaalAnker,
  contract: MijlpaalContractInvoer,
): { dag: string | null; reden: ContractMijlpaalOverslaanReden | null } {
  switch (anker) {
    case 'ONDERTEKEND':
      return contract.ondertekendOp
        ? { dag: nlDag(contract.ondertekendOp), reden: null }
        : { dag: null, reden: 'NIET_ONDERTEKEND' }
    case 'START':
      return { dag: contract.startDatum, reden: null }
    case 'EINDE_WEEK_1':
      return { dag: contractEindeWeek1(contract.startDatum), reden: null }
    case 'EINDE_MAAND_1':
      return { dag: contractEindeMaand1(contract.startDatum), reden: null }
    case 'PROEFTIJD_EINDE':
      return contract.proeftijdEindDatum
        ? { dag: contract.proeftijdEindDatum, reden: null }
        : { dag: null, reden: 'GEEN_PROEFTIJD' }
    case 'CONTRACT_EINDE':
      return contract.eindDatum
        ? { dag: contract.eindDatum, reden: null }
        : { dag: null, reden: 'ONBEPAALDE_TIJD' }
    default: {
      const _exhaustive: never = anker
      return _exhaustive
    }
  }
}

/**
 * Bereken de mijlpalen van een contract uit het sjabloon. Puur: zelfde invoer
 * geeft altijd dezelfde uitvoer. Uitgeschakelde sjabloonregels leveren geen
 * mijlpaal op; een mijlpaal die niet van toepassing is komt terug met een
 * `overslaanReden`, zodat het dossier kan laten zien waarom hij ontbreekt.
 */
export function berekenMijlpalen(
  contract: MijlpaalContractInvoer,
  sjabloon: MijlpaalSjabloonInvoer,
  opties: MijlpaalPlanOpties = {},
): GeplandeMijlpaal[] {
  const feestdagen = feestdagenAlsDatums(opties.feestdagen)
  const alleenEindreeks = contract.isVerlenging && sjabloon.verlengingAlleenEindreeks
  const geenProeftijd = contract.proeftijdEindDatum === null

  const uit: GeplandeMijlpaal[] = []
  for (const regel of sjabloon.regels) {
    if (!regel.actief) continue

    // De week-1-check zonder proeftijd vraagt een evaluatiegesprek (spec §5.3).
    const variant: ContractMijlpaalVariant =
      regel.code === 'M5' && geenProeftijd ? 'GEEN_PROEFTIJD' : 'STANDAARD'
    const gesprekTypeKey =
      variant === 'GEEN_PROEFTIJD' ? CONTRACT_GESPREK_TYPE_KEYS.EVALUATIE : regel.gesprekTypeKey

    const basis = {
      code: regel.code,
      anker: regel.anker,
      actie: regel.actie,
      tijdstip: regel.tijdstip,
      variant,
      gesprekTypeKey,
    }
    const overgeslagen = (reden: ContractMijlpaalOverslaanReden): GeplandeMijlpaal => ({
      ...basis,
      momentOp: null,
      momentInstant: null,
      opentOp: null,
      deadlineOp: null,
      overslaanReden: reden,
      gesprekUiterlijkOp: null,
    })

    if (alleenEindreeks && CONTRACT_MIJLPAAL_FASE_PER_CODE[regel.code] !== 'VERLENGING') {
      uit.push(overgeslagen('VERLENGING'))
      continue
    }

    const anker = ankerDag(regel.anker, contract)
    if (anker.dag === null) {
      uit.push(overgeslagen(anker.reden ?? 'NIET_ONDERTEKEND'))
      continue
    }

    const momentOp = pasWerkdagCorrectieToe(
      pasOffsetToe(anker.dag, regel.offsetAantal, regel.offsetEenheid),
      regel.werkdagCorrectie,
      feestdagen,
    )
    const opentOp = contractDagPlusDagen(momentOp, -Math.max(0, regel.voorloopDagen))

    let deadlineOp: string | null = null
    if (regel.deadlineAnker) {
      const deadlineBasis = ankerDag(regel.deadlineAnker, contract).dag
      if (deadlineBasis !== null) {
        deadlineOp = pasOffsetToe(
          deadlineBasis,
          regel.deadlineOffsetAantal,
          regel.deadlineOffsetEenheid,
        )
      }
    } else if (regel.termijnDagen !== null) {
      deadlineOp = contractDagPlusDagen(momentOp, Math.max(0, regel.termijnDagen))
    }

    const gesprekUiterlijkOp =
      regel.code === 'M5' && contract.proeftijdEindDatum !== null
        ? uitersteProeftijdGesprekDag(contract.proeftijdEindDatum, opties.feestdagen)
        : null

    uit.push({
      ...basis,
      momentOp,
      momentInstant: amsterdamWallclockToUtc(momentOp, regel.tijdstip ?? '00:00'),
      opentOp,
      deadlineOp,
      overslaanReden: null,
      gesprekUiterlijkOp,
    })
  }
  return uit
}

// ─── Status bij aanmaken ──────────────────────────────────────────────────────

/** De laatste dag waarop een mijlpaal nog zin heeft: de deadline, anders het moment. */
function laatsteZinvolleDag(m: { momentOp: string | null; deadlineOp: string | null }): string | null {
  return m.deadlineOp ?? m.momentOp
}

export interface MijlpaalBeginStatus {
  status: Extract<ContractMijlpaalStatus, 'GEPLAND' | 'OVERGESLAGEN'>
  overslaanReden: ContractMijlpaalOverslaanReden | null
}

/**
 * Met welke status komt een nieuw berekende mijlpaal in de reeks?
 *
 * - Niet van toepassing volgens de planner: OVERGESLAGEN met die reden.
 * - Migratie (de eenmalige vulling voor lopende contracten): elk moment dat al
 *   voorbij is wordt OVERGESLAGEN met reden MIGRATIE, zodat niemand met
 *   terugwerkende kracht een melding krijgt (spec §7).
 * - Normaal: ligt alleen het moment in het verleden, dan gaat de mijlpaal alsnog
 *   open. Is ook de deadline voorbij, dan hangt het af van de soort:
 *   - een MELDING ("start over twee weken") heeft achteraf geen zin meer en wordt
 *     overgeslagen;
 *   - werk (taak, gesprek, verslag) waarvan de deadline korter dan
 *     `CONTRACT_MIJLPAAL_INHAAL_DAGEN` geleden is gaat TOCH open. Hij verloopt dan
 *     direct en escaleert, en dat is precies de bedoeling: een contract dat pas
 *     op de startdag wordt getekend mag de facilities-check niet stil overslaan;
 *   - is de deadline langer geleden, dan wordt hij overgeslagen (een contract dat
 *     maanden na de start wordt geregistreerd krijgt geen stapel oude taken).
 */
export function bepaalBeginStatus(
  plan: Pick<GeplandeMijlpaal, 'momentOp' | 'deadlineOp' | 'overslaanReden' | 'actie'>,
  vandaag: string,
  opties: { migratie?: boolean } = {},
): MijlpaalBeginStatus {
  if (plan.overslaanReden) return { status: 'OVERGESLAGEN', overslaanReden: plan.overslaanReden }
  if (plan.momentOp === null) return { status: 'OVERGESLAGEN', overslaanReden: 'NIET_ONDERTEKEND' }
  if (opties.migratie) {
    return plan.momentOp < vandaag
      ? { status: 'OVERGESLAGEN', overslaanReden: 'MIGRATIE' }
      : { status: 'GEPLAND', overslaanReden: null }
  }
  const laatste = laatsteZinvolleDag(plan)
  if (laatste !== null && laatste < vandaag) {
    const inhaalbaar =
      plan.actie !== 'MELDING' &&
      plan.deadlineOp !== null &&
      plan.deadlineOp >= contractDagPlusDagen(vandaag, -CONTRACT_MIJLPAAL_INHAAL_DAGEN)
    if (!inhaalbaar) return { status: 'OVERGESLAGEN', overslaanReden: 'MOMENT_VERSTREKEN' }
  }
  return { status: 'GEPLAND', overslaanReden: null }
}

// ─── Dagelijkse stap ──────────────────────────────────────────────────────────

export type MijlpaalStap = 'OPENEN' | 'VERLOPEN' | 'GEEN'

/**
 * Wat moet de motor vandaag met deze mijlpaal doen?
 *
 * GEPLAND gaat open zodra de openingsdag is bereikt; OPEN verloopt de dag na de
 * deadline (de deadline zelf telt nog als op tijd). Een mijlpaal die tegelijk
 * moet openen en al voorbij zijn deadline is, gaat eerst open: de aanroeper
 * vraagt daarna de stap opnieuw op en krijgt dan VERLOPEN.
 */
export function bepaalMijlpaalStap(
  m: { status: ContractMijlpaalStatus; opentOp: string | null; deadlineOp: string | null },
  vandaag: string,
): MijlpaalStap {
  if (m.status === 'GEPLAND') {
    return m.opentOp !== null && m.opentOp <= vandaag ? 'OPENEN' : 'GEEN'
  }
  if (m.status === 'OPEN') {
    return m.deadlineOp !== null && vandaag > m.deadlineOp ? 'VERLOPEN' : 'GEEN'
  }
  return 'GEEN'
}

// ─── Herberekening bij een datumwijziging (spec §2.B.3 + §5.2) ────────────────

export interface BestaandeMijlpaal {
  code: ContractMijlpaalCode
  status: ContractMijlpaalStatus
  actie: ContractMijlpaalActie
  momentOp: string | null
  tijdstip: string | null
  opentOp: string | null
  deadlineOp: string | null
  variant: ContractMijlpaalVariant
  gesprekUiterlijkOp: string | null
  overslaanReden: ContractMijlpaalOverslaanReden | null
  /** Er is al een melding over deze mijlpaal verstuurd. */
  meldingVerzonden: boolean
}

export type MijlpaalHerberekening =
  | { soort: 'AANMAKEN'; code: ContractMijlpaalCode; plan: GeplandeMijlpaal; begin: MijlpaalBeginStatus }
  | { soort: 'ONGEWIJZIGD'; code: ContractMijlpaalCode }
  /** Blijft staan zoals hij is (afgerond, handmatig overgeslagen, reeks gestopt). */
  | { soort: 'BEHOUDEN'; code: ContractMijlpaalCode }
  | {
      soort: 'VERSCHUIVEN'
      code: ContractMijlpaalCode
      plan: GeplandeMijlpaal
      nieuweStatus: ContractMijlpaalStatus
      /** Er was al een melding verstuurd: de ontvangers krijgen een correctie. */
      correctie: boolean
    }
  | {
      soort: 'OVERSLAAN'
      code: ContractMijlpaalCode
      /** Null wanneer de mijlpaal niet meer in het sjabloon staat. */
      plan: GeplandeMijlpaal | null
      reden: ContractMijlpaalOverslaanReden
      correctie: boolean
    }
  /** Was overgeslagen en is door de nieuwe datums weer van toepassing. */
  | { soort: 'HERSTELLEN'; code: ContractMijlpaalCode; plan: GeplandeMijlpaal }

const BLIJVENDE_OVERSLAAN_REDENEN: readonly ContractMijlpaalOverslaanReden[] = ['HANDMATIG', 'BEEINDIGD']

function isZelfdePlanning(bestaand: BestaandeMijlpaal, plan: GeplandeMijlpaal): boolean {
  return (
    bestaand.momentOp === plan.momentOp &&
    bestaand.tijdstip === plan.tijdstip &&
    bestaand.opentOp === plan.opentOp &&
    bestaand.deadlineOp === plan.deadlineOp &&
    bestaand.variant === plan.variant &&
    bestaand.gesprekUiterlijkOp === plan.gesprekUiterlijkOp
  )
}

/**
 * Vergelijk de bestaande mijlpalen met een vers berekend plan en bepaal per code
 * wat er moet gebeuren. De regels:
 *
 * Wat al is uitgezet verandert alleen als de DATUMS VAN HET CONTRACT zelf zijn
 * gewijzigd (`opties.datumGewijzigd`: start, einde of proeftijd, door HR). Een
 * ander plan door een aangepast sjabloon of een bijgewerkte feestdagenlijst raakt
 * alleen wat nog GEPLAND staat. Zo krijgt niemand een "gewijzigd"-melding over
 * een medewerker die al maanden werkt omdat HR het sjabloon bijstelde.
 *
 * Met `datumGewijzigd`:
 * - AFGEROND blijft staan. Uitzondering: een verstuurde MELDING neemt de nieuwe
 *   datum over en krijgt een correctie (er valt niets "af te ronden", de melding
 *   noemde alleen een datum die nu anders is).
 * - OPEN en VERLOPEN schuiven mee. Een open mijlpaal blijft open (het werk was al
 *   uitgezet); een verlopen mijlpaal gaat weer open als de nieuwe deadline nog
 *   niet voorbij is. Verlopen doet alleen de dagelijkse stap (met escalatie),
 *   nooit de herberekening zelf.
 * - Is een mijlpaal door de nieuwe gegevens niet meer van toepassing, dan wordt
 *   hij overgeslagen met de afgeleide reden; andersom komt hij terug.
 *
 * Altijd: GEPLAND volgt het nieuwe plan; handmatig overgeslagen en een gestopte
 * reeks blijven zoals ze zijn.
 *
 * `opties.migratie` geldt alleen voor nieuw aan te maken mijlpalen (de eenmalige
 * vulling voor lopende contracten): verstreken momenten worden dan overgeslagen.
 */
export function bepaalHerberekening(
  bestaand: readonly BestaandeMijlpaal[],
  plan: readonly GeplandeMijlpaal[],
  vandaag: string,
  opties: { migratie?: boolean; datumGewijzigd?: boolean } = {},
): MijlpaalHerberekening[] {
  const datumGewijzigd = opties.datumGewijzigd === true
  const perCode = new Map(bestaand.map((b) => [b.code, b]))
  const uit: MijlpaalHerberekening[] = []

  for (const p of plan) {
    const b = perCode.get(p.code)
    if (!b) {
      uit.push({ soort: 'AANMAKEN', code: p.code, plan: p, begin: bepaalBeginStatus(p, vandaag, opties) })
      continue
    }

    if (b.status === 'AFGEROND') {
      if (datumGewijzigd && b.actie === 'MELDING' && p.overslaanReden === null && !isZelfdePlanning(b, p)) {
        uit.push({
          soort: 'VERSCHUIVEN',
          code: p.code,
          plan: p,
          nieuweStatus: 'AFGEROND',
          correctie: b.meldingVerzonden,
        })
      } else {
        uit.push({ soort: 'BEHOUDEN', code: p.code })
      }
      continue
    }

    if (b.status === 'OVERGESLAGEN') {
      if (b.overslaanReden && BLIJVENDE_OVERSLAAN_REDENEN.includes(b.overslaanReden)) {
        uit.push({ soort: 'BEHOUDEN', code: p.code })
        continue
      }
      if (p.overslaanReden !== null) {
        if (p.overslaanReden === b.overslaanReden) uit.push({ soort: 'ONGEWIJZIGD', code: p.code })
        else if (b.overslaanReden === 'MIGRATIE' || b.overslaanReden === 'MOMENT_VERSTREKEN') {
          uit.push({ soort: 'BEHOUDEN', code: p.code })
        } else {
          uit.push({ soort: 'OVERSLAAN', code: p.code, plan: p, reden: p.overslaanReden, correctie: false })
        }
        continue
      }
      // Weer van toepassing. Een moment dat (nog steeds) in het verleden ligt
      // komt niet terug: dat zou alsnog een melding met terugwerkende kracht geven.
      const verleden =
        b.overslaanReden === 'MIGRATIE'
          ? p.momentOp !== null && p.momentOp < vandaag
          : bepaalBeginStatus(p, vandaag).status === 'OVERGESLAGEN'
      if (verleden) {
        if (b.overslaanReden === 'MIGRATIE' || b.overslaanReden === 'MOMENT_VERSTREKEN') {
          uit.push({ soort: 'BEHOUDEN', code: p.code })
        } else {
          uit.push({
            soort: 'OVERSLAAN',
            code: p.code,
            plan: p,
            reden: 'MOMENT_VERSTREKEN',
            correctie: false,
          })
        }
      } else {
        uit.push({ soort: 'HERSTELLEN', code: p.code, plan: p })
      }
      continue
    }

    // GEPLAND, OPEN of VERLOPEN. Wat al is uitgezet blijft zoals het is, tenzij
    // de contractdatums zelf zijn gewijzigd.
    if (b.status !== 'GEPLAND' && !datumGewijzigd) {
      uit.push({ soort: 'BEHOUDEN', code: p.code })
      continue
    }
    if (p.overslaanReden !== null) {
      uit.push({
        soort: 'OVERSLAAN',
        code: p.code,
        plan: p,
        reden: p.overslaanReden,
        correctie: b.meldingVerzonden,
      })
      continue
    }
    if (isZelfdePlanning(b, p)) {
      uit.push({ soort: 'ONGEWIJZIGD', code: p.code })
      continue
    }
    if (b.status === 'GEPLAND') {
      // Nog niets uitgezet: ligt de nieuwe datum helemaal in het verleden, dan
      // heeft openen geen zin meer.
      if (bepaalBeginStatus(p, vandaag).status === 'OVERGESLAGEN') {
        uit.push({
          soort: 'OVERSLAAN',
          code: p.code,
          plan: p,
          reden: 'MOMENT_VERSTREKEN',
          correctie: false,
        })
      } else {
        uit.push({ soort: 'VERSCHUIVEN', code: p.code, plan: p, nieuweStatus: 'GEPLAND', correctie: false })
      }
      continue
    }
    // De herberekening laat nooit zelf verlopen: is de nieuwe deadline al
    // voorbij, dan blijft een open mijlpaal open en doet de dagelijkse stap de
    // rest (met de escalatie die daarbij hoort).
    const deadlineVoorbij = p.deadlineOp !== null && vandaag > p.deadlineOp
    uit.push({
      soort: 'VERSCHUIVEN',
      code: p.code,
      plan: p,
      nieuweStatus: deadlineVoorbij ? b.status : 'OPEN',
      correctie: b.meldingVerzonden,
    })
  }

  // Mijlpalen die niet meer in het sjabloon staan (regel uitgezet): wat nog niet
  // is uitgezet vervalt, wat al open staat of klaar is blijft zoals het is.
  const inPlan = new Set(plan.map((p) => p.code))
  for (const b of bestaand) {
    if (inPlan.has(b.code)) continue
    if (b.status === 'GEPLAND') {
      uit.push({ soort: 'OVERSLAAN', code: b.code, plan: null, reden: 'NIET_IN_SJABLOON', correctie: false })
    } else {
      uit.push({ soort: 'BEHOUDEN', code: b.code })
    }
  }
  return uit
}

// ─── Afrond-controle (spec §5.6 + harde regels uit de reeks) ──────────────────

export interface MijlpaalAfrondGesprek {
  /** NL-kalenderdag van het gesprek. */
  dag: string
  geannuleerd: boolean
}

export interface MijlpaalAfrondInvoer {
  code: ContractMijlpaalCode
  variant: ContractMijlpaalVariant
  checklist: ReadonlyArray<{ gedaan: boolean }>
  /** Het formulier is volledig ingevuld (door de aanroeper met het schema getoetst). */
  formulierVolledig: boolean
  /** Het gesprek dat aan de mijlpaal hangt, of null. */
  gesprek: MijlpaalAfrondGesprek | null
  /** Harde uiterste dag voor dat gesprek (week-1-check met proeftijd). */
  gesprekUiterlijkOp: string | null
  /** M8: het verslag van het einde-proeftijdgesprek. */
  verslag: { vrijgegeven: boolean; akkoord: boolean } | null
  /** M8: de uitkomst die bij het afronden wordt vastgelegd. */
  uitkomst: ContractProeftijdUitkomst | null
  /** M9: het verlengingsbesluit is vastgelegd (FO-388). */
  besluitVastgelegd: boolean
  /** M9: het gesprek met de medewerker, na het interne besluitgesprek. */
  vervolgGesprek: MijlpaalAfrondGesprek | null
}

function nlDatum(dag: string): string {
  const [jaar, maand, d] = dag.split('-')
  return `${d}-${maand}-${jaar}`
}

/** "2 van 6" voor de checklist-stand (ook gebruikt in de escalatiemelding). */
export function beschrijfChecklistStand(checklist: ReadonlyArray<{ gedaan: boolean }>): string {
  return `${checklist.filter((c) => c.gedaan).length} van ${checklist.length}`
}

/**
 * Mag deze mijlpaal worden afgerond? Geeft alles terug wat nog ontbreekt (leeg =
 * afronden kan), zodat de kaart elke melding bij het juiste onderdeel toont in
 * plaats van een generieke fout.
 */
export function controleerMijlpaalAfronden(invoer: MijlpaalAfrondInvoer): ContractMijlpaalBlokkade[] {
  const blokkades: ContractMijlpaalBlokkade[] = []
  const gesprek = invoer.gesprek && !invoer.gesprek.geannuleerd ? invoer.gesprek : null

  if (invoer.checklist.length > 0 && invoer.checklist.some((c) => !c.gedaan)) {
    blokkades.push({
      code: 'CHECKLIST_ONVOLLEDIG',
      veld: 'checklist',
      melding: `Nog niet alles is afgevinkt (${beschrijfChecklistStand(invoer.checklist)}).`,
    })
  }

  if (CONTRACT_MIJLPAAL_FORMULIER_PER_CODE[invoer.code] !== null && !invoer.formulierVolledig) {
    blokkades.push({
      code: 'FORMULIER_ONTBREEKT',
      veld: 'formulier',
      melding: 'Vul eerst het formulier volledig in.',
    })
  }

  if (invoer.code === 'M1' && !gesprek) {
    blokkades.push({
      code: 'GESPREK_NIET_GEPLAND',
      veld: 'gesprek',
      melding: 'Plan eerst het startgesprek.',
    })
  }

  if (invoer.code === 'M5') {
    if (!gesprek) {
      blokkades.push({
        code: 'GESPREK_NIET_GEPLAND',
        veld: 'gesprek',
        melding:
          invoer.variant === 'GEEN_PROEFTIJD'
            ? 'Plan eerst het evaluatiegesprek. Zonder geplande afspraak kan deze taak niet worden afgerond.'
            : 'Plan eerst het einde-proeftijdgesprek. Zonder geplande afspraak kan deze taak niet worden afgerond.',
      })
    } else if (invoer.gesprekUiterlijkOp !== null && gesprek.dag > invoer.gesprekUiterlijkOp) {
      blokkades.push({
        code: 'GESPREK_TE_LAAT',
        veld: 'gesprek',
        melding: `Het einde-proeftijdgesprek moet uiterlijk op ${nlDatum(invoer.gesprekUiterlijkOp)} plaatsvinden (3 werkdagen voor het einde van de proeftijd). Verzet het gesprek naar een eerdere datum.`,
      })
    }
  }

  if (invoer.code === 'M8') {
    if (!gesprek) {
      blokkades.push({
        code: 'GESPREK_NIET_GEPLAND',
        veld: 'gesprek',
        melding: 'Er is nog geen einde-proeftijdgesprek aan deze mijlpaal gekoppeld.',
      })
    }
    if (!invoer.verslag || !invoer.verslag.vrijgegeven) {
      blokkades.push({
        code: 'VERSLAG_ONTBREEKT',
        veld: 'verslag',
        melding: 'Het schriftelijke verslag van het einde-proeftijdgesprek ontbreekt of is nog niet naar de medewerker verzonden.',
      })
    } else if (!invoer.verslag.akkoord) {
      blokkades.push({
        code: 'VERSLAG_NIET_ONDERTEKEND',
        veld: 'verslag',
        melding: 'De medewerker heeft het verslag nog niet ondertekend.',
      })
    }
    if (invoer.uitkomst !== 'VOORTZETTEN' && invoer.uitkomst !== 'BEEINDIGEN') {
      blokkades.push({
        code: 'UITKOMST_ONTBREEKT',
        veld: 'uitkomst',
        melding: 'Leg de uitkomst vast: voortzetten of beëindigen in de proeftijd.',
      })
    }
  }

  if (invoer.code === 'M9') {
    if (!gesprek) {
      blokkades.push({
        code: 'GESPREK_NIET_GEPLAND',
        veld: 'gesprek',
        melding: 'Plan eerst het interne besluitgesprek.',
      })
    }
    if (!invoer.besluitVastgelegd) {
      blokkades.push({
        code: 'BESLUIT_ONTBREEKT',
        veld: 'besluit',
        melding: 'Leg het verlengingsbesluit vast: verlengen, omzetten naar onbepaalde tijd of niet verlengen.',
      })
    }
    const vervolg = invoer.vervolgGesprek && !invoer.vervolgGesprek.geannuleerd ? invoer.vervolgGesprek : null
    if (!vervolg) {
      blokkades.push({
        code: 'VERVOLGGESPREK_NIET_GEPLAND',
        veld: 'gesprek',
        melding: 'Plan na het besluit het gesprek met de medewerker.',
      })
    }
  }

  return blokkades
}

/**
 * Valt een beëindiging op `beeindigingsDag` binnen de proeftijd? De dag waarop de
 * proeftijd afloopt telt nog mee; zonder proeftijd is het antwoord altijd nee.
 */
export function isBeeindigingInProeftijd(
  proeftijdEindDatum: string | null,
  beeindigingsDag: string,
): boolean {
  return proeftijdEindDatum !== null && beeindigingsDag <= proeftijdEindDatum
}
